/**
 * SortableFieldItem Component
 * Draggable item for hide fields dropdown (qualifications and team members)
 */

import { memo } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableFieldItem = memo(({ item, isVisible, onToggle, type }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id });

  // Constrain transform to vertical axis only
  const constrainedTransform = transform ? {
    ...transform,
    x: 0
  } : null;

  const style = {
    transform: CSS.Transform.toString(constrainedTransform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer group bg-white"
      onClick={onToggle}
    >
      <div className="flex items-center gap-2.5 flex-1 min-w-0">
        {/* Toggle */}
        <div className="flex-shrink-0">
          <div className={`relative w-9 h-5 rounded-full transition-colors duration-200 ${isVisible ? 'bg-green-500' : 'bg-gray-300'}`}>
            <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform duration-200 ${isVisible ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </div>

        {/* Avatar (only for members) */}
        {type === 'member' && (
          <div className="flex-shrink-0 w-5 h-5 flex items-center justify-center">
            {item.avatar_url ? (
              <img
                src={item.avatar_url}
                alt={item.name}
                className="w-5 h-5 rounded-full object-cover"
              />
            ) : (
              <div className="w-5 h-5 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center text-gray-700 text-[9px] font-medium">
                {item.name.split(' ').map(n => n[0]).join('').toUpperCase()}
              </div>
            )}
          </div>
        )}

        {/* Name */}
        <span className="text-xs text-gray-700 truncate flex-1">{item.name}</span>
      </div>

      {/* Drag handle - always visible */}
      <div
        {...attributes}
        {...listeners}
        className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 transition-colors"
        onClick={(e) => e.stopPropagation()}
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <circle cx="5" cy="4" r="1" />
          <circle cx="5" cy="8" r="1" />
          <circle cx="5" cy="12" r="1" />
          <circle cx="9" cy="4" r="1" />
          <circle cx="9" cy="8" r="1" />
          <circle cx="9" cy="12" r="1" />
        </svg>
      </div>
    </div>
  );
});

SortableFieldItem.displayName = 'SortableFieldItem';

export default SortableFieldItem;
